import {
  FC,
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from 'react';

import { SelectOption, SelectOptionWithImage } from '~globals/types/commons';
import {
  ItemsSearchFilters,
  useItemsSearchFilters,
} from '~hooks/useItemsSearchFilters';
import { useToggle } from '~hooks/useToggle';
import { getStartEndDates } from '~utils/dates';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';

import { useDashboardVariantV2GiverContext } from './Context';
import { DashboardVariantV2GiverFiltersContextValue } from './types';

const { startDate: startDayDate, endDate: endDayDate } = getStartEndDates({
  unit: 'day',
});

export const DEFAULT_FILTERS_STATE: ItemsSearchFilters = {
  // COMMONS
  search: '',
  expirationDateTimeFrom: null,
  expirationDateTimeTo: null,
  includeWithoutExpirationDate: null,
  dateFrom: null,
  dateTo: null,
  // SPECIFIC ITEMS
  finalizedDateTimeFrom: startDayDate,
  finalizedDateTimeTo: endDayDate,
  unit1Gt: '',
  unit1Lt: '',
  unit2Gt: '',
  unit2Lt: '',
  priceGt: '',
  priceLt: '',
  vehicles: null,
  drivers: null,
  services: null,
  zones: null,
  assignedUser: null,
  sourceImports: null,
  itemState: null,
  pickingState: null,
  // SPECIFIC COLLECT
  giverCompany: null,
  collectState: null,
  giverCollectState: null,
};

export const DEFAULT_FILTERS_EMPTY_STATE: ItemsSearchFilters = {
  ...DEFAULT_FILTERS_STATE,
  finalizedDateTimeFrom: null,
  finalizedDateTimeTo: null,
};

const DashboardVariantV2GiverFinalizedFiltersContext =
  createContext<DashboardVariantV2GiverFiltersContextValue>({
    autocompletesRefs: {
      itemState: { current: null },
      sourceImports: { current: null },
    },
    filters: {
      isFilterApplied: false,
      setIsFilterApplied: () => {},
      filters: DEFAULT_FILTERS_STATE,
      setFilterByKey: () => {},
      handleChangeInputFilter: () => {},
      clearFilters: () => {},
      filtersActives: DEFAULT_FILTERS_STATE,
      setFiltersActives: () => {},
      setFilterActiveByKey: () => {},
      clearFiltersActives: () => {},
      handleApplyFilter: () => {},
      handleResetFilters: () => {},
      handleClearFilters: () => {},
      handleClearFilterByKey: () => {},
      handleClearFilterByValues: () => {},
    },
    forceRequest: { value: false, toggleValue: () => {} },
  });

export const useDashboardVariantV2GiverFinalizedFiltersContext =
  (): DashboardVariantV2GiverFiltersContextValue =>
    useContext(DashboardVariantV2GiverFinalizedFiltersContext);

const DashboardVariantV2GiverFinalizedFiltersProvider: FC = ({ children }) => {
  const {
    actions: {
      ignoreRefreshBySocket: { toggleValue: toggleIgnoreRefreshBySocket },
      expandedHasChangeOrdersAlert: {
        toggleValue: toggleExpandedHasChangeOrdersAlert,
      },
      modifiedItemsIdBySocket: { setValue: setModifiedItemsIdBySocket },
    },
  } = useDashboardVariantV2GiverContext();

  const itemStateAutocompleteRef =
    useRef<CustomAutocompleteRef<SelectOption, true>>(null);

  const sourceImportsAutocompleteRef =
    useRef<CustomAutocompleteRef<SelectOptionWithImage, true>>(null);

  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

  const filters = useItemsSearchFilters(DEFAULT_FILTERS_STATE);

  const [forceRequest, toggleForceRequest] = useToggle(false);

  const handleApplyFilter = useCallback(() => {
    filters.setFiltersActives(filters.filters);

    setIsFilterApplied(true);

    toggleExpandedHasChangeOrdersAlert(false);
    toggleIgnoreRefreshBySocket(false);
    setModifiedItemsIdBySocket([]);

    toggleForceRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.filters,
    filters.setFiltersActives,
    setIsFilterApplied,
    toggleExpandedHasChangeOrdersAlert,
    toggleIgnoreRefreshBySocket,
    setModifiedItemsIdBySocket,
    toggleForceRequest,
  ]);

  const handleResetFilters = useCallback(() => {
    filters.handleChangeFilters?.(filters.filtersActives);

    itemStateAutocompleteRef.current?.setValue(
      filters.filtersActives.itemState ?? [],
    );
    sourceImportsAutocompleteRef.current?.setValue(
      filters.filtersActives.sourceImports ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.filters,
    filters.handleChangeInputFilter,
    filters.filtersActives,
  ]);

  const handleClearFilters = useCallback(
    (forceRequestParam: boolean) => {
      filters.clearFilters(DEFAULT_FILTERS_EMPTY_STATE);
      filters.clearFiltersActives(DEFAULT_FILTERS_EMPTY_STATE);

      itemStateAutocompleteRef.current?.resetAutocomplete();
      sourceImportsAutocompleteRef.current?.resetAutocomplete();

      setIsFilterApplied(false);

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);

      if (forceRequestParam) {
        toggleForceRequest(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.clearFilters,
      filters.clearFiltersActives,
      setIsFilterApplied,
      toggleExpandedHasChangeOrdersAlert,
      toggleIgnoreRefreshBySocket,
      setModifiedItemsIdBySocket,
      toggleForceRequest,
    ],
  );

  const handleClearFilterByKey = useCallback(
    (
      key: string,
      newVal: unknown,
      callback?: (newFilters: Partial<ItemsSearchFilters>) => void,
    ) => {
      filters.setFilterByKey(key, newVal);
      filters.setFilterActiveByKey(key, newVal);

      callback?.({ [key]: newVal });

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.setFilterByKey, filters.setFilterActiveByKey],
  );

  const handleClearFilterByValues = useCallback(
    (
      values: Record<string, unknown>,
      callback?: (newFilters: Partial<ItemsSearchFilters>) => void,
    ) => {
      Object.entries(values).map(([key, val]) => {
        filters.setFilterByKey(key, val);
        filters.setFilterActiveByKey(key, val);
      });

      callback?.(values);

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.setFilterByKey, filters.setFilterActiveByKey],
  );

  return (
    <DashboardVariantV2GiverFinalizedFiltersContext.Provider
      value={{
        autocompletesRefs: {
          sourceImports: sourceImportsAutocompleteRef,
          itemState: itemStateAutocompleteRef,
        },
        filters: {
          ...filters,
          isFilterApplied,
          setIsFilterApplied,
          handleApplyFilter,
          handleResetFilters,
          handleClearFilters,
          handleClearFilterByKey,
          handleClearFilterByValues,
        },
        forceRequest: {
          value: forceRequest,
          toggleValue: toggleForceRequest,
        },
      }}
    >
      {children}
    </DashboardVariantV2GiverFinalizedFiltersContext.Provider>
  );
};

export default DashboardVariantV2GiverFinalizedFiltersProvider;
